import * as React from 'react';
import { useTranslation } from '@shared/core/i18n';
import type { StylingProps } from './PeopleListItem.styles';
import {
  StyledItemInfo,
  AvatarWrapper,
  AvatarBorder,
  ItemText,
  ItemSubtext,
  TextContainer,
  DeliveryIconsRow,
  DeliveryIcon,
  UnreachableRowIndicator,
} from './PeopleListItem.styles';
import { Avatar, AvatarImage, AvatarFallback, Flex, TooltipV2 } from '@withjoy/joykit';
import { Chat, Envelope, WarningTriangle } from '@withjoy/joykit/icons';

export type InGroupPosition = 'first' | 'middle' | 'last' | '';

type SimpleProps = Readonly<{
  avatarUrl?: string;
  labelText: string;
  sublabelText?: string;
  disabled?: boolean;
  inGroupPosition?: InGroupPosition;
  isSmall?: boolean;
  compact?: boolean;
  showSublabelWhenSmall?: boolean;
  avatarName?: string;
  rightContent?: React.ReactNode;
}>;

const getAvatarSize = (isSmall?: boolean, compact?: boolean): 'sm' | 'md' | 'lg' | number => {
  if (compact) return isSmall ? 20 : 'sm';
  return isSmall ? 'sm' : 'md';
};

const Simple: React.FC<SimpleProps> = React.memo(
  ({ avatarUrl, labelText, sublabelText, disabled, isSmall, compact, showSublabelWhenSmall, inGroupPosition, avatarName, rightContent }) => {
    const isNoNamePlusOne = inGroupPosition !== 'first' && !avatarName;
    const fallbackContent = avatarName ? undefined : inGroupPosition !== 'first' ? '+1' : '?';
    const subProps: StylingProps = { isSmall, compact, inGroupPosition, disabled, isNoNamePlusOne };
    return (
      <StyledItemInfo {...subProps}>
        <AvatarWrapper>
          <Avatar
            size={getAvatarSize(isSmall, compact)}
            name={avatarName || undefined}
          >
            {avatarUrl ? (
              <AvatarImage
                src={avatarUrl}
                alt={labelText + ' photo'}
              />
            ) : null}
            <AvatarFallback>{fallbackContent}</AvatarFallback>
          </Avatar>
          {inGroupPosition && <AvatarBorder />}
        </AvatarWrapper>
        <Flex
          flexDirection="column"
          overflow="hidden"
          flex={1}
        >
          <TextContainer>
            <ItemText>{labelText}</ItemText>
            {(!isSmall || showSublabelWhenSmall) && sublabelText && <ItemSubtext>{sublabelText}</ItemSubtext>}
          </TextContainer>
        </Flex>
        {rightContent}
      </StyledItemInfo>
    );
  },
);

export type WithDeliveryMethodProps = SimpleProps &
  Readonly<{
    id: string;
    selected: boolean;
    enableSmsSelection: boolean;
    email?: string;
    hasPhoneNumber?: boolean;
    /** contact_service marked the number `invalid`. Adds a warning beside the text icon; selection is untouched. */
    phoneInvalid?: boolean;
    hasEmailDeliveryMethodSelected?: boolean;
    hasTextDeliveryMethodSelected?: boolean;
    shouldHideEmailIcon?: boolean;
    shouldHidePhoneIcon?: boolean;
  }>;

const WithDeliveryMethod: React.FC<WithDeliveryMethodProps> = React.memo(
  ({
    id,
    selected,
    email,
    enableSmsSelection,
    hasPhoneNumber,
    phoneInvalid,
    hasEmailDeliveryMethodSelected,
    hasTextDeliveryMethodSelected,
    shouldHideEmailIcon,
    shouldHidePhoneIcon,
    labelText,
    ...baseProps
  }) => {
    const { tq } = useTranslation('peoplePicker');
    const isUnreachable = selected ? !hasEmailDeliveryMethodSelected && !hasTextDeliveryMethodSelected : !email && !hasPhoneNumber;

    const unreachableTooltip = isUnreachable ? (!hasPhoneNumber && !email ? tq`No contact information` : !hasPhoneNumber ? tq`No phone number` : tq`No email address`) : '';

    const showTextIcon = enableSmsSelection && !isUnreachable && !shouldHidePhoneIcon && !!hasPhoneNumber;

    const deliveryIcons = (
      <DeliveryIconsRow>
        {showTextIcon && phoneInvalid && (
          <TooltipV2
            content={tq`This number may not receive texts`}
            placement="top"
          >
            <UnreachableRowIndicator aria-label={tq`This number may not receive texts`}>
              <WarningTriangle size={14} />
            </UnreachableRowIndicator>
          </TooltipV2>
        )}
        {showTextIcon && (
          <DeliveryIcon aria-label={hasTextDeliveryMethodSelected ? tq`Text: sending` : tq`Text: available`}>
            <Chat size={16} />
          </DeliveryIcon>
        )}
        {!isUnreachable && !shouldHideEmailIcon && email && (
          <DeliveryIcon aria-label={hasEmailDeliveryMethodSelected ? tq`Email: sending` : tq`Email: available`}>
            <Envelope size={16} />
          </DeliveryIcon>
        )}
        {isUnreachable && (
          <TooltipV2
            content={unreachableTooltip}
            placement="top"
          >
            <UnreachableRowIndicator>
              <WarningTriangle size={14} />
            </UnreachableRowIndicator>
          </TooltipV2>
        )}
      </DeliveryIconsRow>
    );

    return (
      <Simple
        labelText={labelText}
        {...baseProps}
        rightContent={deliveryIcons}
      />
    );
  },
);

export const PeopleListItem = {
  Simple,
  WithDeliveryMethod,
};
